/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUniversity, 
  faGraduationCap, 
  faUserGraduate,
  faArrowLeft,
  faGlobe,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../config/api';
import { formatEmployerName } from '../utils/nameFormatter';
import '../styles/Companies.css';
import '../styles/Universities.css';

const Universities = () => {
  const navigate = useNavigate();
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUniversities = async () => {
      try {
        setLoading(true);
        const response = await axios.get(buildApiUrl('/api/university/list'));
        const universities = response.data?.universities || response.data?.companies || [];

        setUniversities(universities);
      } catch (error) {
        console.error('❌ Error fetching universities:', error);
        setUniversities([]);
      } finally {
        setLoading(false);
      }
    };

    loadUniversities();
  }, []);

  const stats = useMemo(() => {
    const partnerCount = universities.length;
    const programCount = universities.reduce((count, uni) => {
      if (uni.programHighlights) {
        return count + 1;
      }
      return count;
    }, 0);
    const studentStories = universities.reduce((count, uni) => {
      if (uni.aboutUs) {
        return count + 1;
      }
      return count;
    }, 0);

    return {
      partners: partnerCount,
      programs: programCount,
      stories: studentStories
    };
  }, [universities]);

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="companies-page universities-page">
      <button 
        className="back-to-home-btn" 
        onClick={() => navigate('/')}
        aria-label="Back to Homepage"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
        <span>Back to Home</span>
      </button>

      <div className="companies-hero">
        <div className="companies-hero-overlay"></div>
        <div className="companies-hero-content">
          <div className="hero-badge">
            <FontAwesomeIcon icon={faUniversity} className="badge-icon" />
            <span>ACADEMIC PARTNERS</span>
          </div>
          <h1 className="companies-hero-title">
            Explore Top Universities <br />
            <span className="gradient-text">Partnering For Excellence</span>
          </h1>
          <p className="companies-hero-subtitle">
            Connect with prestigious educational institutions actively collaborating to shape future professionals
          </p>
          <div className="companies-stats">
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faUniversity} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.partners}+</div>
                <div className="stat-label">Partner Universities</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faGraduationCap} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.programs}+</div>
                <div className="stat-label">Programs</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faUserGraduate} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.stories}+</div>
                <div className="stat-label">Success Stories</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>

      <section className="companies-container" style={{ paddingTop: '3rem' }}>
        <div style={{ marginBottom: '2rem', maxWidth: '760px', margin: '0 auto 2rem auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem', textAlign: 'center' }}>
            Featured University Partners
          </h2>
          <p style={{ color: '#4b5563', fontSize: '16px', margin: 0, textAlign: 'center' }}>
            These institutions collaborate with Akshar Jobs to support students, graduates, and alumni.
          </p>
        </div>

        {loading ? (
          <div className="companies-loading">
            <div className="loading-spinner"></div>
            <p>Loading universities...</p>
          </div>
        ) : universities.length === 0 ? (
          <div className="no-companies">
            <FontAwesomeIcon icon={faUniversity} className="no-companies-icon" />
            <h3>No Universities Listed Yet</h3>
            <p>
              To get highlighted here{' '}
              <Link to="/contact-us" style={{ color: '#4fc3f7', textDecoration: 'underline', fontWeight: '600' }}>
                contact us
              </Link>
            </p>
          </div>
        ) : (
          <div className="companies-grid">
            {universities.map((university, index) => {
              const logo = (university.showLogoOnWebsite !== false) ? (university.institutionLogo || university.logo || university.companyLogo || '') : '';
              const companyNameRaw = university.companyName || university.employerName || university.universityName || 'University';
              const companyName = formatEmployerName(companyNameRaw);
              const location = university.campusLocation || university.address || university.country || '';
              const highlights = university.programHighlights || university.aboutUs || '';
              const website = university.website;

              return (
                <div className="company-card" key={university._id || university.id || index}>
                  <div className="company-card-header">
                    <div className="card-header-bg"></div>
                    <div className="company-logo-wrapper">
                      {logo ? (
                        <img
                          src={logo}
                          alt={companyName}
                          className="company-logo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="company-logo-placeholder"
                        style={{ display: logo ? 'none' : 'flex' }}
                      >
                        <span className="initials-text">{getInitials(companyName)}</span>
                      </div>
                    </div>
                    <div className="verified-badge">
                      <FontAwesomeIcon icon={faUniversity} />
                      <span>Academic Partner</span>
                    </div>
                  </div>

                  <div className="company-card-body">
                    <h3 className="company-name">{companyName}</h3>
                    {location && (
                      <div className="company-detail">
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                        <span>{location}</span>
                      </div>
                    )}
                    {highlights && (
                      <p className="company-description">
                        {highlights.length > 140 ? `${highlights.slice(0, 140)}…` : highlights}
                      </p>
                    )}
                  </div>

                  <div className="company-card-footer">
                    {website ? (
                      <a
                        href={website.startsWith('http') ? website : `https://${website}`}
                        className="view-jobs-btn"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FontAwesomeIcon icon={faGlobe} />
                        Visit Website
                      </a>
                    ) : (
                      <button className="view-jobs-btn" disabled>
                        Awaiting Website
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Universities;

